import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  NotificationsActive as NotificationsActiveIcon,
  NotificationsOff as NotificationsOffIcon,
} from '@mui/icons-material';
import { format as formatDate, parseISO } from 'date-fns';
import {
  loyaltyCustomerService,
  loyaltyRewardService,
  type LoyaltyCustomerState,
  type LoyaltyReward,
  type LoyaltyRewardStatus,
} from '../services';
import authService from '../services/authService';
import { useToast } from '../hooks/useToast';

type NotificationsFilter = 'all' | 'enabled' | 'disabled';

const REWARD_STATUS_LABELS: Record<LoyaltyRewardStatus, string> = {
  PENDING: 'Pendente',
  REDEEMED: 'Resgatada',
  EXPIRED: 'Expirada',
};

const PRIMARY_BUTTON_SX = {
  backgroundColor: '#F2C94C',
  color: '#2F2A1D',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#E0B83D',
    boxShadow: 'none',
  },
};

const formatDateTime = (value?: string | Date) => {
  if (!value) return '-';
  try {
    const date = typeof value === 'string' ? parseISO(value) : value;
    return formatDate(date, 'dd/MM/yyyy HH:mm');
  } catch (error) {
    return typeof value === 'string' ? value : value.toISOString();
  }
};

const formatPhoneNumber = (value?: string) => {
  if (!value) return '-';
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 9) {
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  }
  return value;
};

const formatOrdersSinceReward = (value: number) => {
  if (value === 0) return 'Nenhum pedido desde a última recompensa';
  if (value === 1) return '1 pedido desde a última recompensa';
  return `${value} pedidos desde a última recompensa`;
};

const LoyaltyCustomersPage: React.FC = () => {
  const { showError, showSuccess, showInfo } = useToast();
  const userRole = authService.getUserRole();
  const isDeveloper = userRole === 'DEVELOPER';
  const defaultCnpj = authService.getActiveCompanyCnpj() ?? authService.getUser()?.cnpj ?? '';

  const [customers, setCustomers] = useState<LoyaltyCustomerState[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [customerIdFilter, setCustomerIdFilter] = useState('');
  const [notificationsFilter, setNotificationsFilter] = useState<NotificationsFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [cnpjFilter, setCnpjFilter] = useState(isDeveloper ? '' : defaultCnpj);

  const [selectedCustomer, setSelectedCustomer] = useState<LoyaltyCustomerState | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const [rewards, setRewards] = useState<LoyaltyReward[]>([]);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [rewardStatusFilter, setRewardStatusFilter] = useState<'all' | LoyaltyRewardStatus>('all');

  const sanitizedCnpj = useMemo(() => {
    if (!cnpjFilter) return undefined;
    const digits = cnpjFilter.replace(/\D/g, '');
    return digits.length > 0 ? digits : undefined;
  }, [cnpjFilter]);

  const effectiveCnpjForDetails = useMemo(() => {
    if (sanitizedCnpj) return sanitizedCnpj;
    return defaultCnpj || undefined;
  }, [sanitizedCnpj, defaultCnpj]);

  const loadCustomers = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const data = await loyaltyCustomerService.list({
        customerId: customerIdFilter.trim() || undefined,
        notificationsEnabled:
          notificationsFilter === 'all' ? undefined : notificationsFilter === 'enabled',
        cnpj: sanitizedCnpj,
      });
      setCustomers(data);
      if (!data.length) {
        showInfo('Nenhum cliente encontrado para os filtros selecionados.');
      }
    } catch (error: any) {
      console.error('[LoyaltyCustomersPage] loadCustomers error', error);
      const message = error?.message || 'Não foi possível carregar os clientes de fidelidade.';
      setErrorMessage(message);
      showError(message);
    } finally {
      setIsLoading(false);
    }
  }, [customerIdFilter, notificationsFilter, sanitizedCnpj, showError, showInfo]);

  useEffect(() => {
    void loadCustomers();
  }, [loadCustomers]);

  const filteredCustomers = useMemo(() => {
    console.log('[LoyaltyCustomersPage] Filtering customers:', {
      totalCustomers: customers.length,
      searchTerm: searchTerm,
      notificationsFilter: notificationsFilter
    });
    
    const filtered = customers.filter((customer) => {
      // Aplicar filtro de busca por nome/telefone/ID
      if (searchTerm.trim()) {
        const term = searchTerm.trim().toLowerCase();
        const nameMatch = customer.customerName?.toLowerCase().includes(term);
        const phoneMatch = customer.phone?.replace(/\D/g, '').includes(term.replace(/\D/g, ''));
        const idMatch = customer.customerId.toLowerCase().includes(term);
        
        console.log('[LoyaltyCustomersPage] Customer filter check:', {
          customerName: customer.customerName,
          term: term,
          nameMatch: nameMatch,
          phoneMatch: phoneMatch,
          idMatch: idMatch
        });
        
        if (!nameMatch && !phoneMatch && !idMatch) {
          return false;
        }
      }
      
      // Aplicar filtro de notificações
      if (notificationsFilter === 'enabled' && !customer.notificationsEnabled) return false;
      if (notificationsFilter === 'disabled' && customer.notificationsEnabled) return false;
      
      return true;
    });
    
    console.log('[LoyaltyCustomersPage] Filtered result:', {
      filteredCount: filtered.length,
      customerNames: filtered.map(c => c.customerName)
    });
    
    return filtered;
  }, [customers, searchTerm, notificationsFilter]);

  const handleToggleNotifications = async (customer: LoyaltyCustomerState) => {
    try {
      const enable = !customer.notificationsEnabled;
      const updated = await loyaltyCustomerService.toggleNotifications(customer.customerId, enable, {
        cnpj: sanitizedCnpj,
      });
      setCustomers((prev) =>
        prev.map((item) => (item.customerId === customer.customerId ? updated : item)),
      );
      showSuccess(enable ? 'Notificações ativadas.' : 'Notificações desativadas.');
    } catch (error: any) {
      console.error('[LoyaltyCustomersPage] toggleNotifications error', error);
      const message = error?.message || 'Não foi possível atualizar as notificações.';
      showError(message);
    }
  };

  const handleOpenDetails = (customer: LoyaltyCustomerState) => {
    setSelectedCustomer(customer);
    setActiveTab(0);
    setRewardStatusFilter('all');
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedCustomer(null);
    setRewards([]);
    setRewardsLoading(false);
  };

  const loadRewards = useCallback(async () => {
    if (!selectedCustomer || activeTab !== 1) {
      return;
    }
    try {
      setRewardsLoading(true);
      const data = await loyaltyRewardService.list({
        customerId: selectedCustomer.customerId,
        status: rewardStatusFilter === 'all' ? undefined : rewardStatusFilter,
        cnpj: effectiveCnpjForDetails,
      });
      setRewards(data);
    } catch (error: any) {
      console.error('[LoyaltyCustomersPage] loadRewards error', error);
      const message = error?.message || 'Não foi possível carregar as recompensas do cliente.';
      showError(message);
    } finally {
      setRewardsLoading(false);
    }
  }, [selectedCustomer, rewardStatusFilter, activeTab, effectiveCnpjForDetails, showError]);

  useEffect(() => {
    void loadRewards();
  }, [loadRewards]);

  const renderCustomerSummary = () => {
    if (!selectedCustomer) {
      return null;
    }

    return (
      <Stack spacing={3}>
        <Paper sx={{ p: 2, backgroundColor: '#FFFFFF', border: '1px solid #E4E7EC', borderRadius: 2 }} elevation={0}>
          <Typography variant="subtitle1" gutterBottom>
            Notificações
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body1">{selectedCustomer.customerName ?? 'Sem nome informado'}</Typography>
            <Typography variant="body2" color="text.secondary">
              Telefone: {formatPhoneNumber(selectedCustomer.phone)}
            </Typography>
            {selectedCustomer.email && (
              <Typography variant="body2" color="text.secondary">
                E-mail: {selectedCustomer.email}
              </Typography>
            )}
          </Stack>
        </Paper>

        <Paper sx={{ p: 2, backgroundColor: '#FFFFFF', border: '1px solid #E4E7EC', borderRadius: 2 }} elevation={0}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Progresso
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
            <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Total de pedidos
                </Typography>
                <Typography variant="h6">{selectedCustomer.totalOrders}</Typography>
              </Stack>
            </Box>
            <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Sequência semanal atual
                </Typography>
                <Typography variant="h6">{selectedCustomer.currentWeekSequence}</Typography>
              </Stack>
            </Box>
            <Box sx={{ width: '100%' }}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Último pedido
                </Typography>
                <Typography variant="body1">{formatDateTime(selectedCustomer.lastOrderAt)}</Typography>
              </Stack>
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ p: 2, backgroundColor: '#FFFFFF', border: '1px solid #E4E7EC', borderRadius: 2 }} elevation={0}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Pontuação recente
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Em breve, histórico detalhado do engajamento.
          </Typography>
        </Paper>
      </Stack>
    );
  };

  const renderCustomerRewards = () => {
    if (!selectedCustomer) {
      return null;
    }

    if (rewardsLoading) {
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Carregando recompensas...
          </Typography>
        </Stack>
      );
    }

    if (rewards.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Nenhuma recompensa encontrada.
        </Typography>
      );
    }

    return (
      <Stack spacing={2}>
        {rewards.map((reward) => (
          <Paper
            variant="outlined"
            key={reward.id}
            sx={{ p: 2, backgroundColor: '#FFFFFF', border: '1px solid #E4E7EC', borderRadius: 2 }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
              <Box>
                <Typography variant="subtitle2">{reward.rewardDescription || reward.id}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Emitida em {formatDateTime(reward.issuedAt)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Status: {REWARD_STATUS_LABELS[reward.status]}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Valor
                </Typography>
                <Typography variant="body1">
                  {reward.rewardType === 'COUPON'
                    ? `R$ ${reward.couponValue?.toFixed(2) ?? '0,00'}`
                    : reward.rewardValue || '-'}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  };

  return (
    <Box sx={{ backgroundColor: '#F5F6FA', minHeight: '100vh', py: { xs: 2, md: 3 } }}>
      <Container maxWidth="lg" sx={{ pb: { xs: 4, md: 6 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" mb={3} spacing={2}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Clientes de Fidelidade
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Gerencie os clientes participantes, controle notificações e visualize o progresso individual.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => void loadCustomers()}
            disabled={isLoading}
            sx={{ ...PRIMARY_BUTTON_SX, alignSelf: { xs: 'stretch', md: 'auto' } }}
          >
            Atualizar lista
          </Button>
        </Stack>

        <Paper
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: '#FFFFFF',
            borderRadius: 2,
            border: '1px solid #E4E7EC',
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Filtros de Busca
          </Typography>
          
          <Stack spacing={3}>
            {/* Primeira linha - Campos principais */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Pesquisar Cliente"
                  placeholder="Nome, telefone ou ID do cliente"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box sx={{ minWidth: { xs: '100%', md: '200px' } }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="notifications-filter-label">Status Notificações</InputLabel>
                  <Select
                    labelId="notifications-filter-label"
                    label="Status Notificações"
                    value={notificationsFilter}
                    onChange={(event) => setNotificationsFilter(event.target.value as NotificationsFilter)}
                  >
                    <MenuItem value="all">Todas</MenuItem>
                    <MenuItem value="enabled">Apenas ativadas</MenuItem>
                    <MenuItem value="disabled">Apenas desativadas</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Stack>

            {/* Segunda linha - Filtros específicos */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="end">
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="ID do Cliente (Específico)"
                  placeholder="Digite o ID exato do cliente"
                  value={customerIdFilter}
                  onChange={(event) => setCustomerIdFilter(event.target.value)}
                />
              </Box>
              {isDeveloper && (
                <Box sx={{ minWidth: { xs: '100%', md: '200px' } }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="CNPJ"
                    value={cnpjFilter}
                    onChange={(event) => setCnpjFilter(event.target.value)}
                    placeholder="Somente números"
                  />
                </Box>
              )}
              <Box sx={{ minWidth: { xs: '100%', md: '140px' } }}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={() => void loadCustomers()}
                  disabled={isLoading}
                  sx={{ 
                    ...PRIMARY_BUTTON_SX, 
                    height: '40px',
                    width: '100%'
                  }}
                >
                  Consultar
                </Button>
              </Box>
            </Stack>

            {/* Indicador de filtros ativos */}
            {(searchTerm || customerIdFilter || notificationsFilter !== 'all' || (isDeveloper && cnpjFilter)) && (
              <Box sx={{ pt: 1, borderTop: '1px solid #E4E7EC' }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Filtros ativos:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                  {searchTerm && (
                    <Chip 
                      size="small" 
                      label={`Busca: "${searchTerm}"`} 
                      onDelete={() => setSearchTerm('')}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {customerIdFilter && (
                    <Chip 
                      size="small" 
                      label={`ID: ${customerIdFilter}`} 
                      onDelete={() => setCustomerIdFilter('')}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {notificationsFilter !== 'all' && (
                    <Chip 
                      size="small" 
                      label={`Notificações: ${notificationsFilter === 'enabled' ? 'Ativadas' : 'Desativadas'}`} 
                      onDelete={() => setNotificationsFilter('all')}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {isDeveloper && cnpjFilter && (
                    <Chip 
                      size="small" 
                      label={`CNPJ: ${cnpjFilter}`} 
                      onDelete={() => setCnpjFilter('')}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        </Paper>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Paper sx={{ borderRadius: 2, border: '1px solid #E4E7EC', backgroundColor: '#FFFFFF' }}>
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Telefone</TableCell>
                  <TableCell align="right">Total de pedidos</TableCell>
                  <TableCell align="right">Sequência semanal</TableCell>
                  <TableCell>Status notificações</TableCell>
                  <TableCell>Último pedido</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                          Carregando clientes...
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Nenhum cliente encontrado.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCustomers.map((customer) => (
                    <TableRow key={customer.customerId} hover>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="subtitle2">{customer.customerName ?? 'Sem nome'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {customer.customerId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatOrdersSinceReward(customer.ordersSinceLastReward)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{formatPhoneNumber(customer.phone)}</TableCell>
                      <TableCell align="right">{customer.totalOrders}</TableCell>
                      <TableCell align="right">{customer.currentWeekSequence}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Switch
                            checked={customer.notificationsEnabled}
                            onChange={() => void handleToggleNotifications(customer)}
                            color="primary"
                          />
                          <Chip
                            size="small"
                            label={customer.notificationsEnabled ? 'Ativadas' : 'Desativadas'}
                            color={customer.notificationsEnabled ? 'success' : 'default'}
                            icon={
                              customer.notificationsEnabled ? (
                                <NotificationsActiveIcon fontSize="small" />
                              ) : (
                                <NotificationsOffIcon fontSize="small" />
                              )
                            }
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>{formatDateTime(customer.lastOrderAt)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver detalhes">
                          <IconButton color="primary" onClick={() => handleOpenDetails(customer)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={handleCloseDrawer}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: 480, md: 520 },
              backgroundColor: '#FFFFFF',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
            <Box>
              <Typography variant="h6">Detalhes do cliente</Typography>
              {selectedCustomer && (
                <Typography variant="body2" color="text.secondary">
                  {selectedCustomer.customerName ?? 'Sem nome'} — ID {selectedCustomer.customerId}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleCloseDrawer}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          {selectedCustomer ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                variant="fullWidth"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="Resumo" />
                <Tab label="Recompensas" />
                <Tab label="Registros (em breve)" disabled />
              </Tabs>

              <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                {activeTab === 0 && renderCustomerSummary()}
                {activeTab === 1 && renderCustomerRewards()}
                {activeTab === 2 && (
                  <Alert severity="info">
                    Os registros detalhados de pedidos serão exibidos aqui assim que o relatório de fidelidade estiver disponível.
                  </Alert>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Selecione um cliente para visualizar os detalhes.
              </Typography>
            </Box>
          )}
        </Drawer>
      </Container>
    </Box>
  );
};

export default LoyaltyCustomersPage;
